import NutrientViewer, {
  AnnotationsUnion,
  FormField,
  Instance,
} from '@nutrient-sdk/viewer';
import { AnnotationTypeEnum } from './types';

export const userChange = async (
  currUser: any,
  PSPDFKit: any,
  instance: any,
  // switchToggle?: any,
  // onChangeReadyToSign?: any,
) => {
  if (instance) {
    const formFields = await instance.getFormFields();
    const signatureFormFields = formFields.filter(
      (field: any) => field instanceof PSPDFKit.FormFields.SignatureFormField,
    );
    const signatureAnnotations = async () => {
      const annotations: any[] = [];
      for (let i = 0; i < instance.totalPageCount; i++) {
        const ann = await instance.getAnnotations(i);
        ann.forEach((annotation: any) => {
          if (
            annotation.customData &&
            (annotation.customData.type === AnnotationTypeEnum.SIGNATURE ||
              annotation.customData.type === AnnotationTypeEnum.INITIAL ||
              annotation.customData.type === AnnotationTypeEnum.DATE_SIGNED ||
              annotation.customData.type ===
                AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED)
          ) {
            if (
              annotation.customData.signerID &&
              annotation.customData.signerID === currUser.id
            ) {
              annotations.push(annotation.name);
            } else if (!annotation.customData.signerID) {
              annotations.push(annotation.name);
            }
          } else {
            annotations.push(annotation.name);
          }
        });
      }
      return annotations;
    };
    const userFieldIds = await signatureAnnotations();
    const readOnlyFormFields = signatureFormFields
      .map((it: any) => {
        if (userFieldIds.includes(it.name)) {
          return it.set('readOnly', false);
        } else {
          return it.set('readOnly', true);
        }
      })
      .filter(Boolean); // Filter out undefined values
    await instance.update(readOnlyFormFields);
  }
};

const createFormField = async (
  type: string,
  annotation: any,
  newId: string,
  instance: any,
) => {
  let options = NutrientViewer.Immutable.List.of();
  let defaultValue = false;
  let customData = null;
  let radiosInUnison: boolean | undefined = undefined;

  const fieldsToGenerateOptions: string[] = [
    AnnotationTypeEnum.CheckBox,
    AnnotationTypeEnum.ComboBox,
  ];

  try {
    if (instance) {
      const formFields = await instance.getFormFields();
      const formFieldsArray = formFields?.toArray?.() || formFields;

      if (formFieldsArray && Array.isArray(formFieldsArray)) {
        const originalFormField = formFieldsArray.find(
          (field: any) =>
            field.name === annotation.formFieldName ||
            field.name === annotation.name ||
            field.id === annotation.id,
        );

        if (originalFormField) {
          if (originalFormField.options) {
            options = originalFormField.options;
          }

          if (
            type === AnnotationTypeEnum.RadioButton &&
            originalFormField.radiosInUnison !== undefined
          ) {
            radiosInUnison = originalFormField.radiosInUnison;
          }

          if (originalFormField.defaultValue !== undefined) {
            defaultValue = originalFormField.defaultValue ?? defaultValue;
          }
          if (originalFormField.customData) {
            customData = originalFormField.customData;

            if (customData.type === AnnotationTypeEnum.DATE_SIGNED) {
              customData.relatedSignatureFieldId = newId;
            }

            if (
              customData.type === AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED
            ) {
              customData.relatedDateFieldId = newId;
            }
          }
        }
      }
    }
  } catch (error) {
    console.warn(`Could not copy customData from original ${type}:`, error);
  }

  // We are generating options to Checkbox and ComboBox if they are missing/empty
  // For RadioButton, we only generate options if they're completely missing
  if (
    (options.size === 0 && fieldsToGenerateOptions.includes(type)) ||
    (type === AnnotationTypeEnum.RadioButton && options.size === 0)
  ) {
    options = NutrientViewer.Immutable.List.of(
      new NutrientViewer.FormOption({
        label: 'Option 1',
        value: '1',
      }),
    );
  }

  const formFieldType = (() => {
    switch (type) {
      case 'TextField':
        return NutrientViewer.FormFields.TextFormField;
      case 'CheckBox':
        return NutrientViewer.FormFields.CheckBoxFormField;
      case 'RadioButton':
        return NutrientViewer.FormFields.RadioButtonFormField;
      case 'ComboBox':
        return NutrientViewer.FormFields.ComboBoxFormField;
      case 'DateAndTimeField':
      case 'dateSigned':
        return NutrientViewer.FormFields.TextFormField;
      case 'signature':
      case 'SIGNATURE':
        return NutrientViewer.FormFields.SignatureFormField;
      case 'initial':
      case 'INITIAL':
        return NutrientViewer.FormFields.SignatureFormField;
      case 'signatureWithDateSigned':
      case 'SIGNATURE_WITH_DATE_SIGNED':
        return NutrientViewer.FormFields.SignatureFormField;
      default:
        return null;
    }
  })();

  if (!formFieldType) {
    return null;
  }

  const formField = new formFieldType({
    annotationIds: NutrientViewer.Immutable.List.of(newId),
    id: newId,
    name: newId,
    label: `${type} Field`,
    maxLength: 100,
    multiLine: false,
    ...(options.size > 0 && { options }),
    ...(defaultValue !== undefined && { defaultValue }),
    ...(customData && { customData }),
    ...(radiosInUnison !== undefined && { radiosInUnison }),
  });

  return formField;
};

// Helper function to create form field for duplicated annotation
const createFormFieldForAnnotation = async (
  annotation: any,
  newId: string,
  instance: any,
) => {
  const annotationType = annotation.customData?.['type'];

  if (!annotationType) return null;

  try {
    return await createFormField(annotationType, annotation, newId, instance);
  } catch (error) {
    console.error('Error creating form field for annotation:', error);
    return null;
  }
};

/**
 * Handles duplication of radio button annotations
 */
const handleRadioButtonDuplication = async (
  tempAnnotation: {
    annotation: AnnotationsUnion;
    formField: FormField | null;
    originalAnnotation: AnnotationsUnion;
    isRadioButton: boolean;
  },
  instance: Instance,
  storeBboxCallback?: (annotationId: string, bbox: number[]) => void,
) => {
  const formFields = await instance.getFormFields();
  const existingFormField = formFields.find(
    (f: FormField) =>
      f.name === tempAnnotation.originalAnnotation.formFieldName,
  );

  if (existingFormField) {
    const existingOptions =
      existingFormField['options'] || NutrientViewer.Immutable.List.of();
    const nextOptionNumber = existingOptions.size + 1;
    const newOption = new NutrientViewer.FormOption({
      label: `Option ${nextOptionNumber}`,
      value: `${nextOptionNumber}`,
    });

    await instance.create(tempAnnotation.annotation);
    await instance.ensureChangesSaved(tempAnnotation.annotation);

    const updatedFormField = existingFormField
      .set(
        'annotationIds',
        existingFormField.annotationIds.push(tempAnnotation.annotation.id),
      )
      .set('options', existingOptions.push(newOption));

    await instance.update(updatedFormField);
    await instance.ensureChangesSaved(updatedFormField);

    // Store the bounding box for manual injection later
    if (storeBboxCallback) {
      const bbox = [
        tempAnnotation.annotation.boundingBox.left,
        tempAnnotation.annotation.boundingBox.top,
        tempAnnotation.annotation.boundingBox.width,
        tempAnnotation.annotation.boundingBox.height,
      ];
      storeBboxCallback(tempAnnotation.annotation.id, bbox);
    }
  }
};

/**
 * Handles duplication of regular (non-radio button) annotations
 */
const handleRegularAnnotationDuplication = async (
  tempAnnotation: {
    annotation: AnnotationsUnion;
    formField: FormField | null;
    originalAnnotation: AnnotationsUnion;
    isRadioButton: boolean;
  },
  instance: Instance,
) => {
  const itemsToCreate: (AnnotationsUnion | FormField)[] = [
    tempAnnotation.annotation,
  ];

  if (tempAnnotation.formField) {
    itemsToCreate.push(tempAnnotation.formField);
  }

  await instance.create(itemsToCreate);
  await instance.ensureChangesSaved(itemsToCreate);
};

/**
 * Updates cross-references between signature and date annotations for signature with date signed
 */
const updateSignatureWithDateSignedCrossReferences = async (
  tempDuplicatedAnnotations: {
    annotation: AnnotationsUnion;
    formField: FormField | null;
    originalAnnotation: AnnotationsUnion;
    isRadioButton: boolean;
  }[],
  instance: Instance,
) => {
  const duplicatedSignature = tempDuplicatedAnnotations.find(
    (combination) =>
      combination.annotation['customData']?.['type'] ===
      AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED,
  );
  const duplicatedDate = tempDuplicatedAnnotations.find(
    (combination) =>
      combination.annotation['customData']?.['type'] ===
      AnnotationTypeEnum.DATE_SIGNED,
  );

  if (duplicatedSignature && duplicatedDate) {
    // Update the signature annotation with the date field reference
    if (duplicatedSignature.annotation.customData) {
      const updatedSignature = duplicatedSignature.annotation.set(
        'customData',
        {
          ...duplicatedSignature.annotation.customData,
          relatedDateFieldId: duplicatedDate.annotation.id,
        },
      );
      await instance.update(updatedSignature);
    }

    // Update the date annotation with the signature field reference
    if (duplicatedDate.annotation.customData) {
      const updatedDate = duplicatedDate.annotation.set('customData', {
        ...duplicatedDate.annotation.customData,
        relatedSignatureFieldId: duplicatedSignature.annotation.id,
      });
      await instance.update(updatedDate);
    }
  }
};

// Complete duplicate annotation tooltip callback function
export const duplicateAnnotationCallback = async (
  annotation: AnnotationsUnion,
  instance: Instance,
  storeBboxCallback?: (annotationId: string, bbox: number[]) => void,
) => {
  // Duplicating does not make sense for a markup annotation
  if (annotation instanceof NutrientViewer.Annotations.MarkupAnnotation) {
    return;
  }

  let isSignatureWithDateSigned = false;
  const annotationsToProcess: AnnotationsUnion[] = [annotation];

  if (
    annotation.customData?.['type'] ===
    AnnotationTypeEnum.SIGNATURE_WITH_DATE_SIGNED
  ) {
    isSignatureWithDateSigned = true;
    const relatedDateFieldId = annotation.customData?.['relatedDateFieldId'];
    const relatedDateFieldAnnotation = (
      await instance.getAnnotations(annotation.pageIndex)
    ).find((ann: AnnotationsUnion) => ann.id === relatedDateFieldId);

    if (relatedDateFieldAnnotation) {
      annotationsToProcess.push(relatedDateFieldAnnotation);
    }
  }

  const tempDuplicatedAnnotations: {
    annotation: AnnotationsUnion;
    formField: FormField | null;
    originalAnnotation: AnnotationsUnion;
    isRadioButton: boolean;
  }[] = [];

  for (const currentAnnotation of annotationsToProcess) {
    const { newId, duplicatedAnnotation, isRadioButton } =
      await generateDuplicateAnnotation(currentAnnotation);

    const formField = isRadioButton
      ? null
      : await createFormFieldForAnnotation(currentAnnotation, newId, instance);

    tempDuplicatedAnnotations.push({
      annotation: duplicatedAnnotation,
      formField: formField as FormField,
      originalAnnotation: currentAnnotation,
      isRadioButton,
    });
  }

  for (const tempAnnotation of tempDuplicatedAnnotations) {
    if (tempAnnotation.isRadioButton) {
      await handleRadioButtonDuplication(
        tempAnnotation,
        instance,
        storeBboxCallback,
      );
    } else {
      await handleRegularAnnotationDuplication(tempAnnotation, instance);
    }
  }

  // Handle cross-references for signature with date signed after all annotations are created
  if (isSignatureWithDateSigned) {
    await updateSignatureWithDateSignedCrossReferences(
      tempDuplicatedAnnotations,
      instance,
    );
  }
};

const generateDuplicateAnnotation = async (
  currentAnnotation: AnnotationsUnion,
) => {
  const offset = 20;
  const offsetTranslation = new NutrientViewer.Geometry.Point({
    x: offset,
    y: offset,
  });

  const newBoundingBox =
    currentAnnotation.boundingBox.translate(offsetTranslation);
  const newId = NutrientViewer.generateInstantId();
  const isRadioButton =
    currentAnnotation.customData?.['type'] === AnnotationTypeEnum.RadioButton;

  let duplicatedAnnotation = currentAnnotation
    .set('id', newId)
    .set('boundingBox', newBoundingBox);

  if (currentAnnotation.customData) {
    duplicatedAnnotation = duplicatedAnnotation.set(
      'customData',
      currentAnnotation.customData,
    );
  }

  if (currentAnnotation.formFieldName) {
    duplicatedAnnotation = duplicatedAnnotation.set(
      'formFieldName',
      isRadioButton ? currentAnnotation.formFieldName : newId,
    );
  }

  if (currentAnnotation.name) {
    duplicatedAnnotation = duplicatedAnnotation.set('name', newId);
  }

  if (
    duplicatedAnnotation instanceof NutrientViewer.Annotations.InkAnnotation
  ) {
    duplicatedAnnotation = duplicatedAnnotation.set(
      'lines',
      duplicatedAnnotation.lines.map((line) => {
        return line.map((point) => point.translate(offsetTranslation));
      }),
    );
  }

  if (
    duplicatedAnnotation instanceof NutrientViewer.Annotations.LineAnnotation
  ) {
    duplicatedAnnotation = duplicatedAnnotation
      .set(
        'startPoint',
        duplicatedAnnotation.startPoint.translate(offsetTranslation),
      )
      .set(
        'endPoint',
        duplicatedAnnotation.endPoint.translate(offsetTranslation),
      );
  }

  if (
    duplicatedAnnotation instanceof
      NutrientViewer.Annotations.PolygonAnnotation ||
    duplicatedAnnotation instanceof
      NutrientViewer.Annotations.PolylineAnnotation
  ) {
    duplicatedAnnotation = duplicatedAnnotation.set(
      'points',
      duplicatedAnnotation.points.map((point) =>
        point.translate(offsetTranslation),
      ),
    );
  }

  return { newId, duplicatedAnnotation, isRadioButton };
};
